import { FormPasswordfield } from '@backoffice-frontend/forms'
import { usePaceValidators } from '@backoffice-frontend/validators'
import { Callout, Heading, Text } from '@moia-dev/pace-web'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type ChangePasswordValues = {
  newPasswordRepeat: string
  newPassword: string
  oldPassword: string
}

export const ChangePasswordForm = ({
  formId,
  errorMessage,
  changeSuccess,
  onSubmit,
}: {
  formId: string
  errorMessage?: string
  changeSuccess: boolean
  onSubmit: (values: ChangePasswordValues) => Promise<void>
}) => {
  const { t } = useTranslation()
  const { required } = usePaceValidators()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<ChangePasswordValues>()

  const newPassword = useWatch({ control, name: 'newPassword' })

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <input hidden type="text" name="username" autoComplete="username" />

      <div
        css={theme => ({
          marginBottom: theme.space.Space4,
        })}
      >
        <Heading
          size="xs"
          css={theme => ({
            display: 'block',
            marginBottom: theme.space.Space2,
          })}
        >
          {t('common_passwordPolicy_header')}
        </Heading>
        <Text
          size="sm"
          css={{
            whiteSpace: 'pre-line',
          }}
        >
          {t('common_passwordPolicy')}
        </Text>
      </div>

      <div
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space3,
        })}
      >
        <FormPasswordfield
          control={control}
          name="oldPassword"
          label={t('authenticatedAppView_oldPassword_label')}
          rules={{ required }}
          disabled={isSubmitting}
          autoComplete="current-password"
          size="compact"
        />

        <FormPasswordfield
          control={control}
          name="newPassword"
          label={t('authenticatedAppView_newPassword_label')}
          rules={{ required }}
          disabled={isSubmitting}
          autoComplete="new-password"
          size="compact"
        />

        <FormPasswordfield
          control={control}
          name="newPasswordRepeat"
          label={t('authenticatedAppView_newPasswordRepeat_label')}
          rules={{
            required,
            validate: value =>
              value === newPassword || t('common_newPasswordMismatch_error'),
          }}
          disabled={isSubmitting}
          autoComplete="new-password"
          size="compact"
        />
      </div>

      {errorMessage && (
        <div css={theme => ({ marginTop: theme.space.Space3 })}>
          <Callout variant="critical">{errorMessage}</Callout>
        </div>
      )}

      {changeSuccess && (
        <div css={theme => ({ marginTop: theme.space.Space3 })}>
          <Callout variant="success">
            {t('authenticatedAppView_passwordChangedSuccessfully')}
          </Callout>
        </div>
      )}
    </form>
  )
}
